import Link from 'next/link';
import { coverBackground, type LearningItem } from '@akademi/site-builder-runtime';
import { Card } from '@akademi/ui';
import { cardReset } from '../_shared/styles';

/**
 * Compact card used by every {@code LearningRailFrame} consumer that
 * doesn't have a more specific card. Adapts to whatever fields the
 * item actually has — image, title, subtitle/instructor, meta.
 *
 * <p>Generic enough for courses, news articles, events, in-person
 * events. Theme-overridable variants (academic/marketplace/cinematic
 * course cards) live in {@code cards/}; this is the lowest-common-
 * denominator rendering.
 */
export function LearningItemCard({
  item,
  hrefTemplate,
  kind = 'course',
}: {
  item: LearningItem;
  hrefTemplate: string;
  kind?: 'course' | 'article' | 'event' | 'category';
}) {
  const href = hrefTemplate.replace('{id}', encodeURIComponent(item.id));
  const cover = item.imageUrl;

  let metaLine: string | undefined;
  if (kind === 'event') {
    const parts: string[] = [];
    if (item.city) parts.push(item.city);
    if (item.startDate) parts.push(new Date(item.startDate).toLocaleDateString());
    if (item.durationHours) parts.push(`${item.durationHours}h`);
    metaLine = parts.join(' · ') || undefined;
  } else if (kind === 'article') {
    const parts: string[] = [];
    if (item.date) parts.push(item.date);
    if (item.readTime) parts.push(item.readTime);
    metaLine = parts.join(' · ') || undefined;
  } else {
    const parts: string[] = [];
    if (item.instructor) parts.push(item.instructor);
    if (item.duration) parts.push(item.duration);
    metaLine = parts.join(' · ') || undefined;
  }

  return (
    <Card asChild>
      <Link
        href={href}
        style={{
          ...cardReset,
          display: 'flex',
          flexDirection: 'column',
          textDecoration: 'none',
          color: 'inherit',
          background: 'var(--color-surface-card, #fff)',
          borderRadius: 'var(--radius-md, 8px)',
          overflow: 'hidden',
        }}
      >
        <div
          aria-hidden
          style={{
            aspectRatio: '16 / 10',
            ...coverBackground(cover),
          }}
        />
        <div style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 4 }}>
          {item.badge?.label && (
            <span
              style={{
                fontSize: 'var(--font-size-xs, 12px)',
                fontWeight: 700,
                letterSpacing: 'var(--font-tracking-wider, 0.06em)',
                textTransform: 'uppercase',
                color: 'var(--color-brand-600, #4f46e5)',
              }}
            >
              {item.badge.label}
            </span>
          )}
          <div
            style={{ fontSize: 'var(--font-size-base, 15px)', fontWeight: 600, lineHeight: 1.3 }}
          >
            {item.title}
          </div>
          {item.subtitle && (
            <div
              style={{
                fontSize: '13px',
                color: 'var(--color-text-secondary, #71717a)',
                lineHeight: 1.4,
              }}
            >
              {item.subtitle}
            </div>
          )}
          {metaLine && (
            <div
              style={{
                marginTop: 2,
                fontSize: 'var(--font-size-xs, 12px)',
                color: 'var(--color-text-tertiary, #94867d)',
              }}
            >
              {metaLine}
            </div>
          )}
          {typeof item.ratingAverage === 'number' && (
            <div
              style={{
                fontSize: 'var(--font-size-xs, 12px)',
                color: 'var(--color-text-secondary, #71717a)',
              }}
            >
              ★ {item.ratingAverage.toFixed(1)}
              {item.ratingCount ? ` (${item.ratingCount.toLocaleString()})` : ''}
            </div>
          )}
        </div>
      </Link>
    </Card>
  );
}
